import {ENDPOINT_ORGANIZATION_RELATIONSHIP} from 'common/endpoints';
import {buildOrgRelationshipOrganizationProgramsUrl} from 'common/utils';
import {commonJwtHeader} from 'common/utils';
import _ from 'lodash';

const getOrgPrograms = (token, orgId, parentOrgId, renderType) => {
  const hasBoth =
    orgId != undefined && parentOrgId !== undefined && orgId !== parentOrgId;
  const orgRenderType = renderType != undefined && renderType == 'org';
  // if renderType is 'org'-> orgId is orgId. if renderType is 'suborg'-> orgId is parentOrgId
  // there is no suborg id if there's only 1 unique id
  return getAllOrganizationProgramsByParams(token, {
    orgId: orgRenderType ? orgId : parentOrgId,
    subOrgId: hasBoth ? orgId : undefined,
    isRestricted: true,
  });
};

const getOrgSARPrograms = (token, orgId, parentOrgId) => {
  const hasBoth =
    orgId != undefined && parentOrgId !== undefined && orgId !== parentOrgId;

  // parent org id becomes the org id,  if both exist and are different
  // there is no suborg id if there's only 1 unique id
  return getAllOrganizationProgramsByParams(token, {
    orgId: parentOrgId,
    subOrgId: hasBoth ? orgId : undefined,
    isRestricted: true,
  });
};

const getAllOrganizationProgramsUnrestricted = (token) => {
  return getAllOrganizationProgramsByParams(token, {
    isRestricted: false,
  });
};

const getAllUnrestrictedOrganizationProgramsByOrgId = (token, orgId) => {
  return getAllOrganizationProgramsByParams(token, {
    orgId: orgId,
    isRestricted: false,
  });
};

const getAllOrganizationProgramsByParams = (token, {isRestricted, orgId, subOrgId}) => {
  const endPointToUse =
    ENDPOINT_ORGANIZATION_RELATIONSHIP +
    buildOrgRelationshipOrganizationProgramsUrl(isRestricted, orgId, subOrgId);

  return fetch(endPointToUse, {
    method: 'GET',
    headers: commonJwtHeader(),
  });
};

const getSubOrgProgramsByParentOrgIdAndSubOrgId = (token, parentOrgId, subOrgId) => {
  const endpointToUse = `${ENDPOINT_ORGANIZATION_RELATIONSHIP}suborganizationprograms?subOrganizationId=${subOrgId}&organizationId=${parentOrgId}`;

  return fetch(endpointToUse, {
    method: 'GET',
    headers: commonJwtHeader(),
  });
};

const callGetFundingOpportunityOrganizations = ({fundingOpportunityId}) => {
  return getFundingOpportunityOrganizations(fundingOpportunityId);
};

const getFundingOpportunityOrganizations = (fundingOpportunityId) => {
  const endpointToUse = `${ENDPOINT_ORGANIZATION_RELATIONSHIP}fo/${fundingOpportunityId}/organizations`;
  return fetch(endpointToUse, {
    method: 'GET',
    headers: commonJwtHeader(),
  });
};

const callGetEntityByOrgIdFOId = ({organizationId, fundingOpportunityId}) => {
  if (organizationId == '-1') {
    return callGetSummativeDataOfAllOrganizationsByFOId(fundingOpportunityId);
  } else {
    return getEntityByOrgIdFOId(organizationId, fundingOpportunityId);
  }
};

const callGetSummativeDataOfAllOrganizationsByFOId = (fundingOpportunityId) => {
  const endpointToUse = `${ENDPOINT_ORGANIZATION_RELATIONSHIP}fo/${fundingOpportunityId}/orgssummativedata`;

  return fetch(endpointToUse, {
    method: 'GET',
    headers: commonJwtHeader(),
  });
};

const getEntityByOrgIdFOId = (organizationId, fundingOpportunityId) => {
  const endpointToUse = `${ENDPOINT_ORGANIZATION_RELATIONSHIP}org/${organizationId}/fo/${fundingOpportunityId}/entity`;

  return fetch(endpointToUse, {
    method: 'GET',
    headers: commonJwtHeader(),
  });
};

const callGetEntityBySubGrants = ({subGrants}) => {
  return Promise.all(
    subGrants.map((subGrant) =>
      getEntityByOrgIdFOId(subGrant.organizationId, subGrant.fundingOpportunityId)
    )
  );
};

const callPutLockInAmounts = ({
  organizationId,
  fundingOpportunityId,
  timeframe,
  lockInAmounts,
  existingData,
  auth,
}) => {
  return putLockInAmounts(
    organizationId,
    fundingOpportunityId,
    timeframe,
    lockInAmounts,
    existingData,
    auth
  );
};

const updateHistory = (body) => {
  const {history = [], ...otherData} = body;
  if (otherData.version !== undefined) {
    history.push(_.cloneDeep(otherData));
  }
  body.history = history;
};

const putLockInAmounts = (
  organizationId,
  fundingOpportunityId,
  timeframe,
  lockInAmounts,
  existingData,
  auth
) => {
  const endpointToUse = `${ENDPOINT_ORGANIZATION_RELATIONSHIP}org/${organizationId}/fo/${fundingOpportunityId}/entity`;
  const body = _.cloneDeep(existingData);

  delete body.loading;

  updateHistory(body);

  const validTimeframes = ['30_days', '6_months', '12_months', 'recalculation'];
  const selectedTimeframe = validTimeframes.includes(timeframe)
    ? timeframe
    : 'recalculation';

  if (body.lockInAmounts === undefined) {
    body.lockInAmounts = {};
  }

  body.lockInAmounts[selectedTimeframe] = {
    projectFundsAmount: lockInAmounts[selectedTimeframe].projectFundsAmount,
    projectFundsObligationLimit:
      lockInAmounts[selectedTimeframe].projectFundsObligationLimit,
    managementCostsAmount: lockInAmounts[selectedTimeframe].managementCostsAmount,
    managementCostsObligationLimit:
      lockInAmounts[selectedTimeframe].managementCostsObligationLimit,
    dateUpdated: Date.now(),
    updatedBy: {
      userId: auth.userId,
      name: `${auth.firstName} ${auth.lastName}`,
    },
  };

  return fetch(endpointToUse, {
    method: 'PUT',
    headers: commonJwtHeader(),
    body: JSON.stringify(body),
  });
};

const callPutFundingCategoryTargets = ({
  organizationId,
  fundingOpportunityId,
  existingData,
  fundingCategoryTargets,
  auth,
}) => {
  return putFundingCategoryTargets(
    organizationId,
    fundingOpportunityId,
    existingData,
    fundingCategoryTargets,
    auth
  );
};

const putFundingCategoryTargets = (
  organizationId,
  fundingOpportunityId,
  existingData,
  fundingCategoryTargets,
  auth
) => {
  const endpointToUse = `${ENDPOINT_ORGANIZATION_RELATIONSHIP}org/${organizationId}/fo/${fundingOpportunityId}/entity`;
  const body = _.cloneDeep(existingData);

  delete body.loading;

  updateHistory(body);

  body.fundingCategoryTargets = {
    ...fundingCategoryTargets,
    dateUpdated: Date.now(),
    updatedBy: {
      userId: auth.userId,
      name: `${auth.firstName} ${auth.lastName}`,
    },
  };

  return fetch(endpointToUse, {
    method: 'PUT',
    headers: commonJwtHeader(),
    body: JSON.stringify(body),
  });
};

const getAllOrganizationsFromFundingOpportunityId = (fundingOpportunityId) => {
  const foId = _.get(fundingOpportunityId, 'fundingOpportunityId', '');
  return fetch(
    `${ENDPOINT_ORGANIZATION_RELATIONSHIP}organizationsDetailsByFoId/${foId}`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

export default {
  getOrgPrograms,
  getAllUnrestrictedOrganizationProgramsByOrgId,
  getAllOrganizationProgramsUnrestricted,
  getSubOrgProgramsByParentOrgIdAndSubOrgId,
  getOrgSARPrograms,
  callGetFundingOpportunityOrganizations,
  getFundingOpportunityOrganizations,
  callGetEntityByOrgIdFOId,
  getEntityByOrgIdFOId,
  callGetEntityBySubGrants,
  callPutLockInAmounts,
  putLockInAmounts,
  callPutFundingCategoryTargets,
  putFundingCategoryTargets,
  getAllOrganizationsFromFundingOpportunityId,
};
